// Smart per-category defaults for unpack flow.
// Frozen pizza → Freezer + 90 days. Bag of chips → Pantry + no expiry.
// Bananas → Fridge + 7 days. User can override per item; this is just the
// first guess so most rows can be confirmed without touching anything.

export type Location = 'fridge' | 'freezer' | 'pantry' | 'other'

export const LOCATIONS: Array<{ id: Location; label: string }> = [
  { id: 'fridge',  label: 'Fridge'  },
  { id: 'freezer', label: 'Freezer' },
  { id: 'pantry',  label: 'Pantry'  },
  { id: 'other',   label: 'Other'   },
]

type CategoryDefault = {
  location:        Location
  tracksExpiry:    boolean
  daysUntilExpiry: number | null
}

const CATEGORY_DEFAULTS: Record<string, CategoryDefault> = {
  produce:       { location: 'fridge',  tracksExpiry: true,  daysUntilExpiry: 7   },
  dairy:         { location: 'fridge',  tracksExpiry: true,  daysUntilExpiry: 14  },
  meat:          { location: 'fridge',  tracksExpiry: true,  daysUntilExpiry: 4   },
  seafood:       { location: 'fridge',  tracksExpiry: true,  daysUntilExpiry: 3   },
  bakery:        { location: 'pantry',  tracksExpiry: true,  daysUntilExpiry: 5   },
  frozen:        { location: 'freezer', tracksExpiry: true,  daysUntilExpiry: 90  },
  pantry:        { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
  snacks:        { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
  beverages:     { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
  alcohol:       { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
  household:     { location: 'other',   tracksExpiry: false, daysUntilExpiry: null },
  personal_care: { location: 'other',   tracksExpiry: false, daysUntilExpiry: null },
  pet:           { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
  other:         { location: 'pantry',  tracksExpiry: false, daysUntilExpiry: null },
}

export function defaultsForCategory(category: string | null | undefined): CategoryDefault {
  const key = (category ?? 'other').toLowerCase().trim()
  return CATEGORY_DEFAULTS[key] ?? CATEGORY_DEFAULTS.other
}

export function suggestedExpiryDate(category: string | null | undefined): string | null {
  const d = defaultsForCategory(category)
  if (d.daysUntilExpiry === null) return null
  const date = new Date()
  date.setDate(date.getDate() + d.daysUntilExpiry)
  return date.toISOString().slice(0, 10)
}

// ── Quantity display ────────────────────────────────────────────────────────
// "×3" for generic counts; "1 gallon" / "19 cans" with pluralization for
// known units. Returns null for null/zero/negative so callers can skip
// rendering cleanly.
//
// Defense in depth against double-pluralization: input is first normalized
// from any plural form ("bottles" → "bottle"), then either matched in the
// PLURAL_UNITS lookup or, as a fallback, suffixed with 's' only when the
// string doesn't already end in s. So "bottles" stored in DB → "2 bottles",
// not "2 bottless".

export const PLURAL_UNITS: Record<string, string> = {
  pint:    'pints',
  can:     'cans',
  bottle:  'bottles',
  box:     'boxes',
  bag:     'bags',
  jar:     'jars',
  pack:    'packs',
  loaf:    'loaves',
  gallon:  'gallons',
  quart:   'quarts',
  dozen:   'dozen',
  bunch:   'bunches',
  head:    'heads',
  package: 'packages',
}

// Plural / alias inputs collapsed back to canonical singular. Generic words
// ('units', 'each') route to 'count' so the formatter renders ×N cleanly.
export const PLURAL_TO_SINGULAR: Record<string, string> = {
  pints:    'pint',
  cans:     'can',
  bottles:  'bottle',
  boxes:    'box',
  bags:     'bag',
  jars:     'jar',
  packs:    'pack',
  loaves:   'loaf',
  loafs:    'loaf',
  gallons:  'gallon',
  quarts:   'quart',
  bunches:  'bunch',
  heads:    'head',
  packages: 'package',
  units:    'count',
  unit:     'count',
  each:     'count',
}

export function formatQuantity(
  quantity: number | null | undefined,
  unit:     string | null | undefined,
): string | null {
  if (quantity == null || quantity <= 0) return null

  const isWhole = Number.isInteger(quantity) || Math.abs(quantity - Math.round(quantity)) < 0.01
  const n       = isWhole ? Math.round(quantity) : Math.round(quantity * 10) / 10

  // Normalize: lowercase + trim + collapse known plurals/aliases to singular.
  const rawUnit  = (unit ?? '').toLowerCase().trim()
  const singular = PLURAL_TO_SINGULAR[rawUnit] ?? rawUnit

  // Generic count → ×N format
  if (!singular || singular === 'count') return `×${n}`

  // Singular form, count of 1
  if (n === 1) return `${n} ${singular}`

  // Known singular → use the lookup
  if (PLURAL_UNITS[singular]) return `${n} ${PLURAL_UNITS[singular]}`

  // Unknown unit fallback: append 's' only if not already plural-looking
  return `${n} ${singular.endsWith('s') ? singular : `${singular}s`}`
}

// Best-effort unit inference from a product name. Receipts often embed the
// unit ("Ben & Jerry's Half Baked, Pint" → 'pint'). Returns null when no
// clean signal is found so the caller can fall back to a category default.
export function inferUnitFromName(name: string): string | null {
  const lower = name.toLowerCase()
  if (/\bpint\b/.test(lower))                          return 'pint'
  if (/\bgallon\b/.test(lower))                        return 'gallon'
  if (/\bquart\b/.test(lower))                         return 'quart'
  if (/\bcan\b/.test(lower))                           return 'can'
  if (/\bbottle\b/.test(lower))                        return 'bottle'
  if (/\bjar\b/.test(lower))                           return 'jar'
  if (/\bbox\b/.test(lower))                           return 'box'
  if (/\bbag\b/.test(lower))                           return 'bag'
  if (/\bpack\b/.test(lower) || /-pack\b/.test(lower)) return 'pack'
  if (/\bloaf\b/.test(lower) || /\bloaves\b/.test(lower)) return 'loaf'
  if (/\bbunch\b/.test(lower))                         return 'bunch'
  if (/\bdozen\b/.test(lower))                         return 'dozen'
  return null
}

export function defaultUnitForCategory(category: string | null | undefined): string {
  switch ((category ?? '').toLowerCase()) {
    case 'beverages': return 'bottle'
    case 'snacks':    return 'bag'
    case 'pet':       return 'can'
    default:          return 'count'
  }
}
